import { Card, Col, Row, Space } from 'antd';
import { useCallback } from 'react';
import { PauseCircleFilled, PlayCircleFilled } from '@ant-design/icons';
import AC from '../../../components/AccessControl';
import Assignment, { AssignButton, SingleAssignmentWizard } from '../../../components/assignments';
import cmsFactory from '../../../components/cmsFactory';
import { TargetIcon } from '../../../components/images/TargetIcon';
import HighlightedContent from '../../../components/panels/HighlightedContent';
import { ModulePreview, ModulePreviewNavigation } from '../../../components/panels/ModulePreview';
import ContentLibraryHeader from '../../../components/navigation/ContentLibraryHeader';
import LanguageIndicator from '../../../components/ui/LanguageIndicator';
import ShareLinkButton from '../../../components/ui/ShareLinkButton';
import Thumbnail from '../../../components/ui/Thumbnail';
import { bilingualText } from '../../../helpers/bilingualText';
import filterContentKeywordsHTML from '../../../helpers/filterContentKeywordsHTML';
import getResourceAssignmentData from '../../../helpers/getResourceAssignmentData';
import htmlStringToReactNodes from '../../../helpers/htmlStringToReactNodes';
import { ReportsAccessProvider } from '../../../lib/ReportsAccessContext';
import { useSession, useHistoryState } from '../../../lib/hooks';
import previewCoverStyles from './preview_cover.module.scss';
const LessonPreview = ({
  data
}) => {
  const {
    user,
    access
  } = useSession();
  const [step, updateStep] = useHistoryState('lesson-preview-step', null);
  const assignment = getResourceAssignmentData(data);
  const onComplete = useCallback(() => updateStep(current => current + 1), [updateStep]);
  const modifiedResourcesHTML = data.resourcesHTML ? filterContentKeywordsHTML(data.resourcesHTML, user.state?.abbreviation, access.system) : null;
  return <div className="preview-module-container" data-sentry-component="LessonPreview" data-sentry-source-file="[slug].jsx">
      <ContentLibraryHeader showBackBtn={true} title={data.name} description={<div className="lesson-header-text" children={htmlStringToReactNodes(data.headerHTML)} />} extra={<LanguageIndicator languages={data.language} />} data-sentry-element="ContentLibraryHeader" data-sentry-source-file="[slug].jsx" />
      <Space direction="vertical" style={{
      display: 'flex'
    }} data-sentry-element="Space" data-sentry-source-file="[slug].jsx">
        <Card data-sentry-element="Card" data-sentry-source-file="[slug].jsx">
          <Row data-sentry-element="Row" data-sentry-source-file="[slug].jsx">
            <Col md={16} sm={24} lg={12} data-sentry-element="Col" data-sentry-source-file="[slug].jsx">
              <div className="preview-column">
                <ModulePreview cover={<>
                      <img src={data.imageURL} alt={data.name} />
                      {step === null ? <button className={previewCoverStyles.button} onClick={() => updateStep(0)}>
                          <PlayCircleFilled size="large" />{' '}
                          <div className={previewCoverStyles.text} children={`${bilingualText(data.language, 'Preview', 'Ver')}`} />
                        </button> : <div className={previewCoverStyles.button}>
                          <PauseCircleFilled size="large" />{' '}
                          <div className={previewCoverStyles.text} children={bilingualText(data.language, 'End of the Lesson', 'Fin de la lección')} />
                        </div>}
                    </>} url={data.activities[step]?.previewURL || null} onComplete={onComplete} data-sentry-element="ModulePreview" data-sentry-source-file="[slug].jsx" />
              </div>
            </Col>
            <Col md={8} sm={24} lg={12} data-sentry-element="Col" data-sentry-source-file="[slug].jsx">
              <div className="resources-information-container">
                <div style={{
                display: 'flex',
                gap: 8
              }}>
                  <ShareLinkButton data-sentry-element="ShareLinkButton" data-sentry-source-file="[slug].jsx" />
                  <ReportsAccessProvider data-sentry-element="ReportsAccessProvider" data-sentry-source-file="[slug].jsx">
                    <Assignment data-sentry-element="Assignment" data-sentry-source-file="[slug].jsx">
                      <SingleAssignmentWizard data-sentry-element="SingleAssignmentWizard" data-sentry-source-file="[slug].jsx" />
                      <AssignButton assignment={assignment} type="primary" data-sentry-element="AssignButton" data-sentry-source-file="[slug].jsx" />
                      <div style={{
                      flexGrow: 1
                    }} />
                      <AC.CheckAccess rule={AC.ruleFromHref({
                      pathname: 'toolkits/assignment-playlist'
                    })} data-sentry-element="unknown" data-sentry-source-file="[slug].jsx">
                        <AssignButton playlist assignment={assignment} type="primary" className="antd-btn-purple" data-sentry-element="AssignButton" data-sentry-source-file="[slug].jsx" />
                      </AC.CheckAccess>
                    </Assignment>
                  </ReportsAccessProvider>
                </div>
                <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                maxHeight: 600,
                overflow: 'auto'
              }}>
                  {data.learningObjectiveHTML && <HighlightedContent title={`${bilingualText(data.language, 'Learning Objective', 'Objetivo de aprendizaje')}`} image={<TargetIcon style={{
                  width: 40
                }} />} content={<div className="highlighted-description" children={htmlStringToReactNodes(data.learningObjectiveHTML)} />} styles={data.language.includes('es') ? {
                  backgroundColor: '#fff8f8',
                  color: '#AC0D0D',
                  border: 'solid 1px #AC0D0D20'
                } : {
                  backgroundColor: '#f2f8fd',
                  color: '#206986',
                  border: 'solid 1px #20698620'
                }} />}
                  <ModulePreviewNavigation title={`${bilingualText(data.language, 'Lesson Activities', 'Actividades')}  (${data.activities.length})`} step={step} updateStep={updateStep} activities={data.activities} parentLanguage={data.language} data-sentry-element="ModulePreviewNavigation" data-sentry-source-file="[slug].jsx" />
                </div>
                {modifiedResourcesHTML && <div className="content resources-list" children={htmlStringToReactNodes(modifiedResourcesHTML)} />}
              </div>
            </Col>
          </Row>
        </Card>
        {data.lessons.length > 0 && <Card>
            <h3 children={`${bilingualText(data.language, 'Related Lessons', 'Lecciones relacionadas')} (${data.lessons.length})`} />
            <div className="library-grid" children={data.lessons.map(lesson => <Thumbnail key={lesson.slug} data={lesson} showText={false} link={{
          pathname: '/toolkits/lessons/[slug]',
          query: {
            slug: lesson.slug
          }
        }} />)} />
          </Card>}
      </Space>
    </div>;
};
export const getStaticProps = context => ({
  props: {
    dataURL: `/content/lessons/${context.params.slug}.json`
  }
});
export const getStaticPaths = () => ({
  fallback: false,
  paths: require('../../../public/content/lessons-paths.json')
});
export default cmsFactory(LessonPreview);